import React from "react";
import { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Paper, Typography, Button, CircularProgress, Tooltip } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import CreateIcon from "@mui/icons-material/Create";
import {
  ApiGet,
  ApiPost,
  ApiPut,
  ApiDelete,
} from "../helpers/API/ApiData";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";

import { DataTable, TableColumnDef } from "../components/DataTable";
import { SelfHostedEditor } from "../components/TextEditor";
import { PageHeader } from "../components/PageHeader";

import { createSanitizedMarkup } from "../utils/createSanitizedMarkup";
import { useContentLanguage } from "../hooks";
import { LanguageTabs } from "../components/LanguageTabs";

interface EditorState {
  content: string;
  content_ger: string;
}

export default function TermsCondition() {
  const [addTerms, setAddTerms] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllTerms, setGetAllTerms] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [TermsIdForDelete, setTermsIdForDelete] = useState<any>();
  const [TermsIdForEdit, setTermsIdForEdit] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(0);

  const [lang, setLang] = useContentLanguage();

  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    content_ger: "",
  });

  useEffect(() => {
    getAllTermsAndConditionData();
  }, [page, countPerPage, search]);

  const addImpreintModelClose = (_e: any) => {
    setAddTerms(false);
    setIsEditApi(false);
    setErrors({});
    setEditorState({ content: "", content_ger: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errors: any = {};
    if (!editorState?.content_ger) {
      formIsValid = false;
      errors["termsAndConditionDescription_ger"] = "*Bitte geben Sie eine Beschreibung ein (DE)";
    }
    if (!editorState?.content) {
      formIsValid = false;
      errors["termsAndConditionDescription"] = "*Bitte geben Sie eine Beschreibung ein (EN)";
    }
    setErrors(errors);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllTermsAndConditionData = async () => {
    setLoaderForGetAll(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`terms-and-conditions/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setGetAllTerms(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const addTermsAndConditionData = async (_e: any) => {
    setIsEditApi(false);
    if (formValidation()) {
      setLoader(true);
      const data = {
        termsAndConditionDescription: editorState?.content,
        termsAndConditionDescription_ger: editorState?.content_ger,
      };

      await ApiPost(`terms-and-conditions/create`, data)
        .then(() => {
          toast.success("Nutzungsbedingungen und Datenschutzrichtlinie erfolgreich erstellen");
          setAddTerms(false);
          setIsEditApi(false);
          getAllTermsAndConditionData();
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const editTermsAndConditionData = async (_e: any) => {
    if (formValidation()) {
      setLoader(true);
      const data = {
        termsAndConditionDescription: editorState?.content,
        termsAndConditionDescription_ger: editorState?.content_ger,
      };

      await ApiPut(`terms-and-conditions/update?id=${TermsIdForEdit}`, data)
        .then(() => {
          toast.success("Nutzungsbedingungen und Datenschutzrichtlinie Update erfolgreich");
          getAllTermsAndConditionData();
          setAddTerms(false);
          setIsEditApi(false);
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const deleteImprintData = async () => {
    await ApiDelete(`terms-and-conditions/delete?id=${TermsIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Nutzungsbedingungen und Datenschutzrichtlinie erfolgreich löschen");
          setShow(false);
          setPage(1);
          getAllTermsAndConditionData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  // Content is sanitized via the createSanitizedMarkup utility (uses DOMPurify)
  const columns: TableColumnDef[] = [
    {
      header: "Beschreibung",
      render: (row: any) => (
        <div
          className="showmore-class"
          style={{ padding: 16 }}
          dangerouslySetInnerHTML={createSanitizedMarkup({
            html: (lang === "de" ? row?.termsAndConditionDescription_ger : row?.termsAndConditionDescription) || "",
          })}
        />
      ),
    },
    {
      header: "Datum",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Nutzungsbedingungen bearbeiten" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setIsEditApi(true);
                setAddTerms(true);
                setTermsIdForEdit(row?._id);
                setEditorState({
                  content: row?.termsAndConditionDescription || "",
                  content_ger: row?.termsAndConditionDescription_ger || "",
                });
              }}
            >
              <CreateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Nutzungsbedingungen löschen" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setTermsIdForDelete(row?._id);
                setShow(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="AGB & Privatsphäre"
            onSearch={(val) => { setSearch(val); setPage(1); }}
            onAdd={() => setAddTerms(true)}
          />
        </Box>
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllTerms || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie dieses Nutzungsbedingungen und
            Datenschutzrichtlinie löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => deleteImprintData()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {addTerms && (
        <Dialog fullScreen open={addTerms}>
          <Toolbar>
            <IconButton edge="end" color="inherit" onClick={addImpreintModelClose} aria-label="close" sx={{ ml: "auto" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deutsch</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>English</Typography>
                </Box>
              </Box>

              {/* Description row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Beschreibung</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content_ger: editor?.getContent() }));
                    }}
                    initialValue={editorState.content_ger}
                  />
                  <Typography variant="caption" color="error">{errors["termsAndConditionDescription_ger"]}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content: editor?.getContent() }));
                    }}
                    initialValue={editorState.content}
                  />
                  <Typography variant="caption" color="error">{errors["termsAndConditionDescription"]}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                disabled={loader}
                onClick={(e) => { isEditApi ? editTermsAndConditionData(e) : addTermsAndConditionData(e); }}
              >
                {isEditApi ? "Speichern" : "Hinzufügen"}
                {loader && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
